import Link from 'next/link';
import { createContext, Dispatch, SetStateAction, useContext, useMemo, useState } from 'react';

import { cookieConfig } from './config';
import { CookieContextProps } from './CookieConsent';
import { useCookieConsent } from './hooks';
import Toggle from '../components/ui/Toggle';

const CookieManager = () => {
  const { services, showManager, needsConsent } = useCookieConsent();

  if (showManager) {
    return <Modal services={services} />;
  }

  if (!needsConsent) return null;

  return <Notice />;
};

const Notice = () => {
  const { acceptAll, revokeAll, openManager, locale } = useCookieConsent();

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 max-h-full overflow-auto pt-4 md:left-auto md:max-w-md">
      <div className="z-50 bg-purple-600 p-4 text-white shadow-lg">
        <h3 className="mb-2 text-xl font-bold">{cookieConfig.i18n[locale].noticeTitle}</h3>
        <p className="mb-4 text-base">
          {cookieConfig.i18n[locale].noticeDescriptionPrefix}
          <span className="font-bold">
            {Object.values(cookieConfig.categories)
              .map((value) => value.label[locale])
              .join(' & ')}
          </span>
          {cookieConfig.i18n[locale].noticeDescriptionSuffix}
        </p>
        <div className="flex flex-col gap-4 sm:flex-row-reverse">
          <button
            className="bg-white px-4 py-2 text-base font-semibold text-purple-600 transition-opacity hover:opacity-75"
            onClick={acceptAll}
            color="purple"
          >
            {cookieConfig.i18n[locale].noticeButtonAccept}
          </button>
          <button
            className="border-2 border-white px-4 py-2 text-base font-semibold text-white transition-opacity hover:opacity-75"
            onClick={revokeAll}
            color="purple"
          >
            {cookieConfig.i18n[locale].noticeButtonDecline}
          </button>
          <button
            className="text-base font-semibold text-white transition-opacity hover:underline"
            onClick={openManager}
          >
            {cookieConfig.i18n[locale].noticeButtonSettings}
          </button>
        </div>
      </div>
    </div>
  );
};

type SelectedServicesContextProps = {
  selectedServices: Record<string, boolean>;
  setSelectedServices: Dispatch<SetStateAction<Record<string, boolean>>>;
};

const SelectedServicesContext = createContext<SelectedServicesContextProps | null>(null);

const Modal = ({ services }: { services: CookieContextProps['services'] }) => {
  const { acceptAll, apply, revokeAll, closeManager, giveConsent, locale } = useCookieConsent();
  const configuredServices = Object.keys(cookieConfig.services).reduce((acc, service) => {
    acc[service] = services[service] || false;
    return acc;
  }, {} as Record<string, boolean>);

  const [selectedServices, setSelectedServices] = useState<Record<string, boolean>>({
    ...configuredServices,
    ...services,
  });

  const handleAcceptSelection = () => {
    apply(selectedServices);
    giveConsent();
    closeManager();
  };

  const handleAcceptAll = () => {
    acceptAll();
    closeManager();
  };

  const handleRevokeAll = () => {
    revokeAll();
    closeManager();
  };

  const groupedServices = useMemo(() => {
    const groupedServices = Object.keys(cookieConfig.services).reduce((acc, service) => {
      const serviceConfig = cookieConfig.services[service];
      const category = serviceConfig.category;

      if (!acc[category]) {
        acc[category] = [];
      }

      acc[category].push(service);

      return acc;
    }, {} as Record<string, string[]>);

    return groupedServices;
  }, []);

  return (
    <SelectedServicesContext.Provider value={{ selectedServices, setSelectedServices }}>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black opacity-50" onClick={closeManager} />
        <div className="z-50 m-4 max-h-full w-full max-w-2xl overflow-auto border-2 border-purple-600 bg-sand-100 p-8 shadow-lg">
          <h3 className="text-2xl font-bold">{cookieConfig.i18n[locale].modalTitle}</h3>
          <p className="mt-4 text-base font-semibold">
            {cookieConfig.i18n[locale].modalDescriptionPrefix}
            {false ? (
              <Link href={'#'}>{cookieConfig.i18n[locale].modalDescriptionLinkLabel}</Link>
            ) : (
              cookieConfig.i18n[locale].modalDescriptionLinkLabel
            )}
            {cookieConfig.i18n[locale].modalDescriptionSuffix}
          </p>
          <div className="mt-8">
            {Object.keys(groupedServices).map((category) => (
              <Category key={category} category={category} groupedServices={groupedServices} />
            ))}
          </div>

          <div className="mt-4 flex flex-col justify-between gap-4 sm:flex-row">
            <button
              className="border-2 border-grey-600 px-4 py-2 text-base font-semibold text-grey-600 transition-opacity hover:opacity-75"
              onClick={handleRevokeAll}
            >
              {cookieConfig.i18n[locale].modalButtonDecline}
            </button>
            <button
              className="bg-grey-600 px-4 py-2 text-base font-semibold text-white transition-opacity hover:opacity-75"
              onClick={handleAcceptSelection}
            >
              {cookieConfig.i18n[locale].modalButtonSave}
            </button>
            <button
              className="bg-purple-600 px-4 py-2 text-base font-semibold text-white transition-opacity hover:opacity-75"
              onClick={handleAcceptAll}
            >
              {cookieConfig.i18n[locale].modalButtonAccept}
            </button>
          </div>
        </div>
      </div>
    </SelectedServicesContext.Provider>
  );
};

const Category = ({ category, groupedServices }: { category: string; groupedServices: Record<string, string[]> }) => {
  const { locale } = useCookieConsent();
  const { selectedServices, setSelectedServices } = useContext(SelectedServicesContext) as SelectedServicesContextProps;
  const label = cookieConfig.categories[category].label[locale];

  const allChecked = groupedServices[category].every((service) => selectedServices[service]);

  const setAllCategoryServices = (checked: boolean) => {
    const newSelectedServices = { ...selectedServices };
    groupedServices[category].forEach((service) => {
      newSelectedServices[service] = checked;
    });
    setSelectedServices(newSelectedServices);
  };

  return (
    <div>
      <div className="mb-4 flex gap-4 border-b-2">
        <Toggle checked={allChecked} onChange={(e) => setAllCategoryServices(e.target.checked)} />
        <h2 className="mb-4 text-xl font-bold">{label}</h2>
      </div>
      <div className="mb-8">
        {groupedServices[category].map((service) => (
          <Service key={service} name={service} />
        ))}
      </div>
    </div>
  );
};

const Service = ({ name }: { name: string }) => {
  const { locale } = useCookieConsent();
  const { selectedServices, setSelectedServices } = useContext(SelectedServicesContext) as SelectedServicesContextProps;

  return (
    <Toggle
      label={cookieConfig.services[name].title}
      description={cookieConfig.services[name].description[locale]}
      checked={selectedServices[name]}
      size="sm"
      onChange={(e) => setSelectedServices((services) => ({ ...services, [name]: e.target.checked }))}
    />
  );
};

export default CookieManager;
